// The TikTok tab: labels, statuses and filters for comments on our own TikTok videos, the saved replies, and the sample comments
// the tab shows until TikTok approves our app (docs/tiktok-comments-api.md). Plain JavaScript so node can test it without a browser.

/** Our TikTok account, as shown on the tab. */
export const tiktokHandle = '@residentservice360';

// Personal details people post in public: a UK mobile or landline, an email address or a full postcode.
const PHONE = /(?:\+44\s?|\b0)(?:7\d{3}|[1-3]\d{2,4})[\s-]?\d{3}[\s-]?\d{3,4}\b/;
const EMAIL = /[^\s@]+@[^\s@]+\.[a-z]{2,}/i;
const POSTCODE = /\b[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}\b/i;
// Words that say someone is asking about housing.
const HOUSING = /\b(rooms?|refer(?:ral|ring)?|apply|homeless|sofa ?surf\w*|evict\w*|housing|accommodation|supported|probation|leaving care|women only|men only|benefits?|universal credit|rent|place to stay|somewhere to (?:live|stay)|how (?:do|can) i)\b/i;

/** `{ personal, housing }`: whether a comment shows someone's personal details, and whether it asks about housing. */
export function labelsFor(text) {
  const words = String(text ?? '');
  return { personal: PHONE.test(words) || EMAIL.test(words) || POSTCODE.test(words), housing: HOUSING.test(words) };
}

/**
 * Where a comment is in the queue: 'hidden' (staff hid it on TikTok), 'done' (marked done), 'replied' (staff have answered it) or
 * 'needs_reply'.
 */
export function statusOf(comment) {
  if (comment.hidden) return 'hidden';
  if (comment.done) return 'done';
  if ((comment.replies || []).some((reply) => reply.staff)) return 'replied';
  return 'needs_reply';
}

const statuses = {
  needs_reply: ['Needs reply', 'warn'],
  replied: ['Replied', 'good'],
  hidden: ['Hidden', 'neutral'],
  done: ['Done', 'good'],
};

/** `{ label, tone }` for a comment's status. */
export function commentStatus(comment) {
  const [label, tone] = statuses[statusOf(comment)];
  return { label, tone };
}

/** The filters above the table, as `[value, words, test]`, in the order they are shown. */
export const commentFilters = [
  ['needs_reply', 'Needs reply', (comment) => statusOf(comment) === 'needs_reply'],
  ['personal', 'Personal details', (comment) => !comment.hidden && labelsFor(comment.text).personal],
  ['replied', 'Replied', (comment) => statusOf(comment) === 'replied'],
  ['hidden', 'Hidden', (comment) => comment.hidden],
  ['done', 'Done', (comment) => statusOf(comment) === 'done'],
  ['all', 'All', () => true],
];

/** The comments that match a filter, and how many match each one: `{ rows, counts }`. */
export function filterComments(comments, filter) {
  const counts = Object.fromEntries(commentFilters.map(([value, , test]) => [value, comments.filter(test).length]));
  const test = (commentFilters.find(([value]) => value === filter) || commentFilters.at(-1))[2];
  return { rows: comments.filter(test), counts };
}

/** Replies staff can start from, each within TikTok's 150 characters. They point people to the self-referral form rather than asking for details in public. */
export const savedReplies = [
  ['refer', 'Refer yourself', "Thanks for asking. The quickest way is to refer yourself at residentservice360.bedmaps.com/enquire and we'll get back to you."],
  ['hidden', "We've hidden your details", "We've hidden your comment to keep your details safe. Refer yourself at residentservice360.bedmaps.com/enquire and we'll be in touch."],
  ['thanks', 'Thanks for sharing', 'Thank you for sharing this. We’re so glad to hear it, and wish you all the best.'],
];

/** "just now", "12 min ago", "3 h ago", "2 days ago". */
export function formatAgo(value, now = Date.now()) {
  const minutes = Math.floor((now - new Date(value).getTime()) / 60000);
  if (!Number.isFinite(minutes)) return '–';
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

// Sample comments, until TikTok approves our app. The people, handles, number (an Ofcom drama number) and email are made up.

const videos = {
  selfReferral: { id: 'video-1', caption: 'How self-referral works, in 60 seconds', days: 2 },
  roomTour: { id: 'video-2', caption: 'Room tour: a shared house in Birmingham', days: 5 },
  explainer: { id: 'video-3', caption: 'What is supported accommodation?', days: 9 },
};

const minutesAgo = (now, minutes) => new Date(now - minutes * 60000).toISOString();

function sample(now, id, video, author, text, minutes, extra = {}) {
  return {
    id,
    video: { id: video.id, caption: video.caption, posted_at: minutesAgo(now, video.days * 1440) },
    author,
    text,
    created_at: minutesAgo(now, minutes),
    likes: 0,
    liked: false,
    hidden: false,
    done: false,
    replies: [],
    activity: [],
    ...extra,
  };
}

/** The comments the tab starts with, timed relative to `now`. */
export function sampleComments(now = Date.now()) {
  const { selfReferral, roomTour, explainer } = videos;
  return [
    sample(now, 'c1', selfReferral, 'jay_m', 'how do I get a room in Leeds? been sofa surfing 3 months', 12),
    sample(now, 'c2', roomTour, 'sam.k_', 'can someone ring me about the room? my number is 07700 900123', 40),
    sample(now, 'c3', selfReferral, 'lou_b', 'got my place last week, thank you all', 125, {
      likes: 14,
      liked: true,
      replies: [{ id: 'c3-r1', author: 'residentservice360', staff: 'Naima', text: 'So glad to hear it, Lou! Wishing you all the best in your new home.', created_at: minutesAgo(now, 70) }],
      activity: [{ what: 'Replied', by: 'Naima', at: minutesAgo(now, 70) }, { what: 'Liked', by: 'Naima', at: minutesAgo(now, 70) }],
    }),
    sample(now, 'c4', explainer, 'marcus.w', 'do you take people on probation?', 190),
    sample(now, 'c5', roomTour, 'aisha_r', 'is the house women only?', 300),
    sample(now, 'c6', explainer, 'dan_p', "what's the difference between this and council housing", 1500, {
      replies: [{ id: 'c6-r1', author: 'residentservice360', staff: 'Naima', text: 'Good question! Supported accommodation comes with a support worker who helps you settle in and move on. Our next video explains it.', created_at: minutesAgo(now, 1380) }],
      activity: [{ what: 'Replied', by: 'Naima', at: minutesAgo(now, 1380) }],
    }),
    sample(now, 'c7', selfReferral, 'deals_4u_99', 'follow me for free giveaways!!! link in bio', 1600, {
      hidden: true,
      activity: [{ what: 'Hidden', by: 'Naima', at: minutesAgo(now, 1590) }],
    }),
    sample(now, 'c8', roomTour, 'kerry.h', 'my son is 19 and leaving care in Coventry, can you email me at kerry.h@example.com', 2900),
  ];
}

/** Comments that "arrive" when staff press "Show a new sample comment", in order: how a comment turns up in the queue. */
export function arrivingComments(now = Date.now()) {
  return [
    sample(now, 'c9', videos.selfReferral, 'ellie.t', "I'm in Manchester and got evicted last week, where do I even start?", 0),
    sample(now, 'c10', videos.explainer, 'ben_j', 'great video, really clear', 0),
  ];
}
